// ---------- pointillist alphabet, three touch sizes, thirteen pigments ----------
//   0        empty cell = bare paper (the highlights)
//   1..39    treize pigments x trois tailles de touche (petite, moyenne, grande)
//            code = (pigment index) * 3 + (size index) + 1
//
// POURQUOI TREIZE ET PAS NEUF. `pointilleElargi` a une roue de neuf teintes avec des trous
// inégaux — jusqu'à 64° entre le violet et la laque de garance, mesuré en HSL. Un banc d'essai
// public (« Atelier des Touches ») a fait tourner le même moteur sur trois images tests (une
// scène de foule, une marine, un paysage) avec 9, 13, 17, 21 puis 25 pigments, toujours les
// mêmes trois tailles de touche : l'indice d'écart colorimétrique chute de 11 à 15 % entre 9 et
// 13 pigments (161,2 → 139,3 sur *La Liberté guidant le peuple*, 146,4 → 130,0 sur *Le Radeau de
// la Méduse*, 115,0 → 99,1 sur le paysage), puis PLAFONNE : passer à 17, 21 ou 25 ne bouge plus
// l'écart de plus de 1 %, alors que le coût en pastilles de légende continue de grimper de façon
// linéaire (40 → 52 → 64 → 76 codes). Le dithering par diffusion d'erreur mélange déjà
// optiquement les teintes voisines sur plusieurs cases ; une fois les plus grands trous de la
// roue comblés, une teinte de plus dans un écart déjà petit n'apporte presque rien qu'il ne
// rendait pas déjà. Treize est le point où le gain s'arrête.
//
// POURQUOI CES QUATRE PIGMENTS. Choisis pour combler, dans l'ordre, les deux plus grands vides
// angulaires de la roue à neuf teintes (mesurés en HSL) : violet → laque de garance (64°) et
// vert émeraude → bleu céruléum (43°). Le bleu de Prusse ne comble pas un trou de teinte mais
// repousse un peu le plancher de noir signalé plus haut dans `pointilleElargi.ts` — la lumi-
// nance de l'outremer (0,20) restait le point le plus sombre atteignable.
//
// TOUJOURS AUCUN NOIR NI GRIS — le principe divisionniste tient tel quel, ce fichier n'y change
// rien.
//
// IDENTIFIANT NEUF PLUTÔT QUE `pointilleElargi` MODIFIÉ EN PLACE, pour la même raison que
// `pointilleElargi` existe à côté de `pointille` : `getPalette()[code].css` n'a pas de garde-
// fou, et un vieux client face à un code au-delà de son tableau planterait au lieu de dessiner
// de travers.

import { rgbOf } from '../ink';
import type { Alphabet } from './types';

const PIGMENTS = [
  { css: '#f2ca35', name: 'jaune de cadmium' },
  { css: '#8cb62c', name: 'vert de vessie' },
  { css: '#2ca272', name: 'vert émeraude' },
  { css: '#2e8ec4', name: 'bleu céruléum' },
  { css: '#1f2f7a', name: 'outremer' },
  { css: '#4a2a6b', name: 'violet de cobalt' },
  { css: '#b23a63', name: 'laque de garance' },
  { css: '#d63f2e', name: 'vermillon' },
  { css: '#e8802c', name: 'orange de cadmium' },
  { css: '#c68a2e', name: 'ocre doré' },
  { css: '#1c9e97', name: 'turquoise de cobalt' },
  { css: '#c23b8f', name: 'fuchsia de quinacridone' },
  { css: '#17335c', name: 'bleu de Prusse' },
];

/** Petit, moyen (la taille de `pointille`), grand — fraction du côté de la case. Identiques à
 *  `pointilleElargi` : ce fichier ne fait varier que le nombre de teintes. */
const SIZES = [0.28, 0.46, 0.62] as const;
const SIZE_NAME = ['petite touche', 'touche moyenne', 'grande touche'];

export interface EffectivePigment {
  r: number;
  g: number;
  b: number;
  css: string;
  name: string;
}

let effective: EffectivePigment[] | null = null;
let paperLum = 0;

function chan(col: string) {
  const [r, g, b] = rgbOf(col);
  return { r, g, b };
}

function pigmentOf(code: number) {
  return PIGMENTS[Math.floor((code - 1) / 3)];
}
function sizeOf(code: number) {
  return SIZES[(code - 1) % 3];
}

function drawRaw(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  code: number,
): void {
  const s = Math.min(w, h);
  ctx.fillStyle = pigmentOf(code).css;
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h / 2, s * sizeOf(code), 0, 7);
  ctx.fill();
}

/**
 * Couverture EFFECTIVE de chaque combinaison teinte+taille — papier compris — pour que
 * l'encodeur apparie contre ce que la case rend vraiment plutôt que contre le pigment pur.
 * Copie de `pointilleElargi.ts::measure`, seule la taille de `PIGMENTS` change.
 */
function measure(): void {
  const S = 24;
  const n = 3;
  const c = document.createElement('canvas');
  c.width = c.height = S * n;
  const ctx = c.getContext('2d')!;
  const paperCss =
    getComputedStyle(document.documentElement).getPropertyValue('--paper').trim() || '#fbf6ec';
  const p = chan(paperCss);
  // Sous les disques le canvas est transparent : le papier vu dessous est assombri de 4 % par
  // le voile de .sheetwrap (cf. pointille.ts, même correction).
  const p0 = { r: p.r * 0.96, g: p.g * 0.96, b: p.b * 0.96 };

  const entries: EffectivePigment[] = [{ ...p0, css: 'transparent', name: 'papier nu' }];
  for (let code = 1; code <= PIGMENTS.length * SIZES.length; code++) {
    ctx.fillStyle = paperCss;
    ctx.fillRect(0, 0, S * n, S * n);
    for (let r = 0; r < n; r++)
      for (let cc = 0; cc < n; cc++) drawRaw(ctx, cc * S, r * S, S, S, code);
    const d = ctx.getImageData(0, 0, S * n, S * n).data;
    let R = 0;
    let G = 0;
    let B = 0;
    for (let i = 0; i < d.length; i += 4) {
      R += d[i];
      G += d[i + 1];
      B += d[i + 2];
    }
    const m = d.length / 4;
    entries.push({ r: R / m, g: G / m, b: B / m, css: pigmentOf(code).css, name: '' });
  }
  effective = entries;
  paperLum = 0.299 * p0.r + 0.587 * p0.g + 0.114 * p0.b;
}

function ready(): void {
  if (!effective) measure();
}

/** Les couleurs effectivement rendues (papier + 39 combinaisons), pour pointilleRiche.encode.ts. */
export function getEffectivePaletteRiche(): EffectivePigment[] {
  ready();
  return effective!;
}

export function paperLuminanceRiche(): number {
  ready();
  return paperLum;
}

export const pointilleRiche: Alphabet = {
  id: 'pointilleRiche',
  sheetMode: 'single',
  bakesOnTop: true,
  label: 'Pointillisme riche',
  symbolName: 'touche',
  selfColored: true,
  helpText:
    'Divisionnisme, en trois grains et treize pigments : quatre teintes de plus que le ' +
    'pointillisme élargi comblent les plus grands vides de la roue des couleurs. Toujours ' +
    'aucun noir ni gris — les valeurs sombres naissent du mélange optique.',

  aspect: () => 1,

  // Ordre de code = ordre de la palette, comme pointilleElargi : ni la teinte ni la taille ne
  // portent d'échelle de couverture à respecter.
  paletteOrder: Array.from({ length: PIGMENTS.length * SIZES.length }, (_, i) => i + 1),

  codeName: (code) => `${pigmentOf(code).name}, ${SIZE_NAME[(code - 1) % 3]}`,

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    drawRaw(ctx, x, y, w, h, code);
  },
};
